/**
 * DarkModeToggle Component
 * Provides a user interface for switching between light, dark, and system themes
 *
 * Features:
 * - Three theme modes: light, dark, system
 * - Accessible design with ARIA labels and keyboard navigation
 * - Theme-aware styling using CSS custom properties
 * - Smooth animations and transitions
 * - Responsive design
 * - System preference indicator
 */

import React from 'react';
import styled from 'styled-components';
import { useTheme } from '../../styles/ThemeProvider';
import type { ThemeMode } from '../../styles/ThemeProvider';

// ============================================================================
// Types & Interfaces
// ============================================================================

export type ThemeToggleVariant = 'button' | 'switch';
export type ThemeToggleSize = 'sm' | 'md' | 'lg';

export interface DarkModeToggleProps {
  /** Visual variant of the toggle */
  variant?: ThemeToggleVariant;
  /** Size of the toggle component */
  size?: ThemeToggleSize;
  /** Whether to show the system option */
  showSystemOption?: boolean;
  /** Custom className for styling */
  className?: string;
  /** Controlled selection, for forms that apply the theme on save. */
  value?: ThemeMode;
  /** Callback when theme changes */
  onThemeChange?: (theme: 'light' | 'dark' | 'system') => void;
}

// ============================================================================
// Styled Components
// ============================================================================

const ToggleContainer = styled.div<{
  $variant: ThemeToggleVariant;
  $size: ThemeToggleSize;
}>`
  display: inline-flex;
  align-items: center;
  gap: ${({ $size }) =>
    $size === 'sm' ? '0.25rem' : $size === 'lg' ? '0.75rem' : '0.5rem'};
  font-family: var(--font-family-base);
`;

const ThemeButton = styled.button<{
  $isActive: boolean;
  $variant: ThemeToggleVariant;
  $size: ThemeToggleSize;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  padding: ${({ $size }) =>
    $size === 'sm'
      ? '0.25rem 0.5rem'
      : $size === 'lg'
        ? '0.75rem 1rem'
        : '0.5rem 0.75rem'};
  border: 2px solid
    ${({ $isActive }) => ($isActive ? 'var(--brand-600)' : 'var(--border)')};
  border-radius: var(--radius-md);
  background: ${({ $isActive }) =>
    $isActive ? 'var(--brand-50)' : 'var(--surface)'};
  color: ${({ $isActive }) =>
    $isActive ? 'var(--brand-700)' : 'var(--text-primary)'};
  font-size: ${({ $size }) =>
    $size === 'sm' ? '0.75rem' : $size === 'lg' ? '1rem' : '0.875rem'};
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: all var(--transition-normal);
  position: relative;
  overflow: hidden;

  /* Focus styles */
  &:focus-visible {
    outline: 3px solid var(--brand-600);
    outline-offset: 2px;
    z-index: 1;
  }

  /* Hover effects */
  &:hover:not(:disabled) {
    background: ${({ $isActive }) =>
      $isActive ? 'var(--brand-100)' : 'var(--interactive-hover)'};

    box-shadow: var(--shadow-sm);
  }

  /* Active state */
  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: none;
  }

  /* Disabled state */
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  /* Icon styling */
  span:first-child {
    font-size: ${({ $size }) =>
      $size === 'sm' ? '1rem' : $size === 'lg' ? '1.25rem' : '1.125rem'};
    line-height: 1;
    flex-shrink: 0;
  }

  /* System indicator */
  .system-indicator {
    position: absolute;
    top: 2px;
    right: 2px;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--brand-500);
    border: 1px solid var(--surface);
    opacity: 0.8;
  }

  /* Dark mode adjustments */
  [data-theme='dark'] & {
    border-color: ${({ $isActive }) =>
      $isActive ? 'var(--brand-400)' : 'var(--border)'};
    background: ${({ $isActive }) =>
      $isActive ? 'rgba(125, 209, 142, 0.1)' : 'var(--surface)'};
    color: ${({ $isActive }) =>
      $isActive ? 'var(--brand-300)' : 'var(--text-primary)'};

    &:hover:not(:disabled) {
      background: ${({ $isActive }) =>
        $isActive ? 'rgba(125, 209, 142, 0.2)' : 'var(--interactive-hover)'};
    }

    .system-indicator {
      background: var(--brand-400);
      border-color: var(--surface-strong);
    }
  }
`;

const SwitchContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  transition: all var(--transition-normal);
`;

const SwitchLabel = styled.span`
  font-size: 0.875rem;
  font-weight: var(--font-weight-medium);
  color: var(--text-primary);
  user-select: none;
`;

const SwitchButton = styled.button<{ $isActive: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border: none;
  border-radius: var(--radius-md);
  background: ${({ $isActive }) =>
    $isActive ? 'var(--brand-600)' : 'transparent'};
  color: ${({ $isActive }) => ($isActive ? 'white' : 'var(--text-muted)')};
  cursor: pointer;
  transition: all var(--transition-normal);
  flex-shrink: 0;

  &:focus-visible {
    outline: 3px solid var(--brand-600);
    outline-offset: 2px;
  }

  &:hover:not(:disabled) {
    background: ${({ $isActive }) =>
      $isActive ? 'var(--brand-700)' : 'var(--interactive-hover)'};
    color: ${({ $isActive }) => ($isActive ? 'white' : 'var(--text-primary)')};
  }

  span {
    font-size: 1.125rem;
    line-height: 1;
  }

  /* Dark mode adjustments */
  [data-theme='dark'] & {
    background: ${({ $isActive }) =>
      $isActive ? 'var(--brand-400)' : 'transparent'};

    &:hover:not(:disabled) {
      background: ${({ $isActive }) =>
        $isActive ? 'var(--brand-300)' : 'var(--interactive-hover)'};
    }
  }
`;

// ============================================================================
// Theme Icons
// ============================================================================

const getThemeIcon = (
  mode: 'light' | 'dark' | 'system',
  size: ThemeToggleSize,
) => {
  const iconSize =
    size === 'sm' ? '1rem' : size === 'lg' ? '1.25rem' : '1.125rem';

  switch (mode) {
    case 'light':
      return <span style={{ fontSize: iconSize }}>☀️</span>;
    case 'dark':
      return <span style={{ fontSize: iconSize }}>🌙</span>;
    case 'system':
      return <span style={{ fontSize: iconSize }}>💻</span>;
    default:
      return null;
  }
};

const getThemeLabel = (
  mode: 'light' | 'dark' | 'system',
  systemTheme?: 'light' | 'dark',
) => {
  switch (mode) {
    case 'light':
      return 'Cambiar a tema claro';
    case 'dark':
      return 'Cambiar a tema oscuro';
    case 'system':
      return `Tema automático (${systemTheme === 'dark' ? 'oscuro' : 'claro'})`;
    default:
      return '';
  }
};

// ============================================================================
// Main Component
// ============================================================================

/**
 * DarkModeToggle - Theme switching component
 *
 * Provides an accessible interface for users to switch between light, dark, and system themes.
 * Integrates with the existing theme system and maintains consistency with the design system.
 */
export const DarkModeToggle: React.FC<DarkModeToggleProps> = ({
  variant = 'button',
  size = 'md',
  showSystemOption = true,
  className,
  value,
  onThemeChange,
}) => {
  const { theme: currentTheme, systemTheme, setTheme } = useTheme();
  const theme = value ?? currentTheme;

  const handleThemeChange = (newTheme: 'light' | 'dark' | 'system') => {
    if (value === undefined) setTheme(newTheme);
    onThemeChange?.(newTheme);
  };

  // Available theme options
  const themeOptions: Array<'light' | 'dark' | 'system'> = showSystemOption
    ? ['light', 'dark', 'system']
    : ['light', 'dark'];

  if (variant === 'switch') {
    return (
      <SwitchContainer className={className}>
        <SwitchLabel>Tema:</SwitchLabel>
        {themeOptions.map((mode) => (
          <SwitchButton
            key={mode}
            $isActive={theme === mode}
            type="button"
            onClick={() => handleThemeChange(mode)}
            aria-label={getThemeLabel(mode, systemTheme)}
            aria-pressed={theme === mode}
            title={getThemeLabel(mode, systemTheme)}
          >
            {getThemeIcon(mode, size)}
            {mode === 'system' && theme === 'system' && (
              <div className="system-indicator" />
            )}
          </SwitchButton>
        ))}
      </SwitchContainer>
    );
  }

  // Default button variant
  return (
    <ToggleContainer $variant={variant} $size={size} className={className}>
      {themeOptions.map((mode) => (
        <ThemeButton
          key={mode}
          $isActive={theme === mode}
          $variant={variant}
          $size={size}
          type="button"
          onClick={() => handleThemeChange(mode)}
          aria-label={getThemeLabel(mode, systemTheme)}
          aria-pressed={theme === mode}
          title={getThemeLabel(mode, systemTheme)}
        >
          {getThemeIcon(mode, size)}
          <span>
            {mode === 'light' && 'Claro'}
            {mode === 'dark' && 'Oscuro'}
            {mode === 'system' && 'Automático'}
          </span>
          {mode === 'system' && theme === 'system' && (
            <div className="system-indicator" />
          )}
        </ThemeButton>
      ))}
    </ToggleContainer>
  );
};

// ============================================================================
// Export
// ============================================================================

export default DarkModeToggle;
